import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { Film, Paintbrush, X } from 'lucide-react'

import {
    IAiModel,
    getPrivateModelCostWithDuration,
    getPublicModelCostWithDuration,
} from 'state/aiModels'
import styles from 'components/quests/ai-magic-wand-menu-button.module.scss'
import {
    PostMediaActionKind,
    listPostMediaModelChoices,
    postMediaAspectRatioOptions,
    postMediaResolutionOptions,
    resolvePostMediaDurationSeconds,
} from './postMediaActions'

export type PostMediaSettingsSelection = {
    aiModelId: string | null
    durationSeconds: number | null
    resolution: string | null
    aspectRatio: string | null
}

const DEFAULT_RESOLUTION = '720p'
const FALLBACK_DURATION_CANDIDATES = [5, 10, 15]

const formatPrice = (price: number) =>
    Number.isFinite(price) && price > 0 ? `$${price.toFixed(2)}` : 'FREE'

const defaultResolutionFor = (model: IAiModel | undefined, requested?: string | null) => {
    const options = postMediaResolutionOptions(model)
    if (!options.length) return null

    const normalizedRequested = String(requested || '')
        .trim()
        .toLowerCase()
    const requestedMatch = options.find(option => option.toLowerCase() === normalizedRequested)
    if (requestedMatch) return requestedMatch

    return options.find(option => option.toLowerCase() === DEFAULT_RESOLUTION) || options[0]
}

const durationOptionsFor = (model: IAiModel | undefined, kind: PostMediaActionKind): number[] => {
    const allowed = (Array.isArray(model?.allowedDurations) ? model!.allowedDurations! : [])
        .map(Number)
        .filter(value => Number.isFinite(value) && value > 0)
    if (allowed.length) return [...new Set(allowed)].sort((left, right) => left - right)

    const candidates = FALLBACK_DURATION_CANDIDATES.map(candidate =>
        Number(resolvePostMediaDurationSeconds(model, kind, candidate)),
    ).filter(value => Number.isFinite(value) && value > 0)
    return [...new Set(candidates)].sort((left, right) => left - right)
}

export default function PostMediaSettingsDialog({
    kind,
    aiModels,
    isPublic,
    initial,
    confirmLabel,
    onConfirm,
    onClose,
}: {
    kind: PostMediaActionKind
    aiModels: IAiModel[] | null | undefined
    isPublic: boolean
    initial?: Partial<PostMediaSettingsSelection> | null
    confirmLabel: string
    onConfirm: (selection: PostMediaSettingsSelection) => void
    onClose: () => void
}) {
    const modelChoices = useMemo(
        () => listPostMediaModelChoices(aiModels, kind) as IAiModel[],
        [aiModels, kind],
    )
    const initialModel =
        modelChoices.find(choice => choice.id === initial?.aiModelId) || modelChoices[0]

    const [aiModelId, setAiModelId] = useState<string | null>(initialModel?.id || null)
    const model = modelChoices.find(choice => choice.id === aiModelId) || initialModel
    const [durationSeconds, setDurationSeconds] = useState<number | null>(() =>
        resolvePostMediaDurationSeconds(model, kind, initial?.durationSeconds),
    )
    const [resolution, setResolution] = useState<string | null>(() =>
        defaultResolutionFor(model, initial?.resolution),
    )
    const [aspectRatio, setAspectRatio] = useState<string | null>(initial?.aspectRatio || null)

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    const priceFor = (candidate: IAiModel | undefined, seconds: number | null) =>
        isPublic
            ? getPublicModelCostWithDuration(candidate, seconds)
            : getPrivateModelCostWithDuration(candidate, seconds)

    const selectModel = (choice: IAiModel) => {
        setAiModelId(choice.id || null)
        setDurationSeconds(resolvePostMediaDurationSeconds(choice, kind, durationSeconds))
        setResolution(current => defaultResolutionFor(choice, current))
        setAspectRatio(current =>
            current && postMediaAspectRatioOptions(choice, kind).includes(current) ? current : null,
        )
    }

    const durationOptions = durationOptionsFor(model, kind)
    const resolutionOptions = postMediaResolutionOptions(model)
    const aspectOptions = postMediaAspectRatioOptions(model, kind)
    const HeaderIcon = kind === 'film' ? Film : Paintbrush
    const headerTitle = kind === 'film' ? 'Film settings' : 'Illustration settings'
    const headerPrice = priceFor(model, durationSeconds)

    const confirm = () =>
        onConfirm({
            aiModelId: model?.id || null,
            durationSeconds,
            resolution,
            aspectRatio: aspectRatio || null,
        })

    return createPortal(
        <div
            className={styles.aiModelSettingsOverlay}
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div
                className={styles.aiModelSettingsDialog}
                data-testid="post-media-settings-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="post-media-settings-title"
                onMouseDown={event => event.stopPropagation()}
            >
                <div className={styles.aiModelSettingsHeader}>
                    <div>
                        <div className={styles.aiModelSettingsTypeRow}>
                            <span className={styles.aiModelSettingsTypeIcon}>
                                <HeaderIcon size={12} />
                            </span>
                            <span>{model?.humanName || model?.displayName || 'AI model'}</span>
                        </div>
                        <h3 id="post-media-settings-title">{headerTitle}</h3>
                        <p>{formatPrice(headerPrice)}</p>
                    </div>
                    <button
                        className={styles.aiModelModalClose}
                        type="button"
                        aria-label="Close settings"
                        onClick={onClose}
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className={styles.aiModelSettingsBody}>
                    <div className={styles.aiModelSettingsGroup}>
                        <div className={styles.aiModelSettingsLabel}>
                            <span>Model</span>
                        </div>
                        <div className={styles.aiModelSettingsOptions}>
                            {modelChoices.map(choice => (
                                <button
                                    key={choice.id}
                                    className={cn(
                                        styles.aiModelSettingsOption,
                                        choice.id === model?.id &&
                                            styles.aiModelSettingsOptionActive,
                                    )}
                                    type="button"
                                    onClick={() => selectModel(choice)}
                                >
                                    <span>
                                        {choice.humanName || choice.displayName || choice.name}
                                    </span>
                                    <span>
                                        {formatPrice(
                                            priceFor(
                                                choice,
                                                resolvePostMediaDurationSeconds(
                                                    choice,
                                                    kind,
                                                    durationSeconds,
                                                ),
                                            ),
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {durationOptions.length > 0 && (
                        <div className={styles.aiModelSettingsGroup}>
                            <div className={styles.aiModelSettingsLabel}>
                                <span>Duration</span>
                            </div>
                            <div className={styles.aiModelSettingsOptions}>
                                {durationOptions.map(option => (
                                    <button
                                        key={option}
                                        className={cn(
                                            styles.aiModelSettingsOption,
                                            option === durationSeconds &&
                                                styles.aiModelSettingsOptionActive,
                                        )}
                                        type="button"
                                        onClick={() => setDurationSeconds(option)}
                                    >
                                        <span>{option}s</span>
                                        <span>{formatPrice(priceFor(model, option))}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {resolutionOptions.length > 0 && (
                        <div className={styles.aiModelSettingsGroup}>
                            <div className={styles.aiModelSettingsLabel}>
                                <span>Resolution</span>
                            </div>
                            <div className={styles.aiModelSettingsOptions}>
                                {resolutionOptions.map(option => (
                                    <button
                                        key={option}
                                        className={cn(
                                            styles.aiModelSettingsOption,
                                            option === resolution &&
                                                styles.aiModelSettingsOptionActive,
                                        )}
                                        type="button"
                                        onClick={() => setResolution(option)}
                                    >
                                        <span>{option}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {aspectOptions.length > 0 && (
                        <div className={styles.aiModelSettingsGroup}>
                            <div className={styles.aiModelSettingsLabel}>
                                <span>Aspect Ratio</span>
                            </div>
                            <div className={styles.aiModelSettingsOptions}>
                                {['', ...aspectOptions].map(option => (
                                    <button
                                        key={option || 'auto'}
                                        className={cn(
                                            styles.aiModelSettingsOption,
                                            (aspectRatio || '') === option &&
                                                styles.aiModelSettingsOptionActive,
                                        )}
                                        type="button"
                                        onClick={() => setAspectRatio(option || null)}
                                    >
                                        <span>{option || 'Auto'}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.aiModelSettingsFooter}>
                    <button
                        className={styles.aiInputSecondaryButton}
                        type="button"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button className={styles.aiInputPrimaryButton} type="button" onClick={confirm}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    )
}
